import * as React from 'react';
import { Select } from 'baseui/select';
import { Body2, Title4 } from 'components/Typography';
import { DynaVault } from '../../../api/DynaVaultsAPI';
import {
  getRiskLevelColorClass,
  getRiskLevelLabel,
  getVaultAPY,
  getVaultAPYPrefix,
} from 'structure/pv2/vault/utils/vault';
import { cx } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';
import { formatPercent } from '@dapp/multi-lib/lib/formatters';

interface InputVaultSelectProps {
  name: string;
  label?: React.ReactNode;
  options: DynaVault[];
  value: DynaVault;
  error?: string | undefined;
  onChange: (address: string) => void;
}

export const InputVaultSelect = (props: InputVaultSelectProps) => {
  const { options, value } = props;

  const _value = options.find((o) => o.address === value.address) ?? null;

  return (
    <Select
      searchable={false}
      getValueLabel={({ option }) => {
        const vault = option as DynaVault;
        return (
          <div className='flex w-full flex-row items-center justify-between gap-2'>
            <Title4 className='text-[1rem] font-normal'>{vault.symbol}</Title4>
            <VaultApy className='text-[1.125rem]' vault={vault} />
          </div>
        );
      }}
      getOptionLabel={({ option, optionState }) => {
        const vault = option as DynaVault;
        return (
          <div className='flex w-full flex-col gap-1'>
            <div className='flex flex-row justify-between'>
              <Title4
                className={cx('text-[1rem] font-normal', {
                  'font-bold': optionState.$selected,
                })}
              >
                {vault.symbol}
              </Title4>
              <div className='flex flex-row gap-1'>
                <Body2 className='text-[1rem]'>APY:</Body2>
                <VaultApy className='text-[1rem]' vault={vault} />
              </div>
            </div>
            <div className='flex flex-row gap-1'>
              <Body2 className='text-[1rem]'>RiskLevel:</Body2>
              <Body2
                className={cx(
                  'text-[1rem]',
                  getRiskLevelColorClass(vault.riskLevel)
                )}
              >
                {getRiskLevelLabel(vault.riskLevel)}
              </Body2>
            </div>
          </div>
        );
      }}
      options={options}
      labelKey='id'
      valueKey='address'
      value={value && _value ? [_value] : []}
      deleteRemoves={false}
      multi={false}
      placeholder='Select token'
      overrides={{
        Root: {
          props: {
            className: 'Root',
          },
        },
        ControlContainer: {
          props: {
            className:
              'ControlContainer !bg-[#111111] !rounded-main !border !border-[#B5B5B5]',
          },
        },
        ValueContainer: {
          props: {
            className: 'ValueContainer w-full !text-[#FFFFFF]',
          },
        },
        OptionContent: {
          props: {
            className: 'OptionContent w-full !text-[#FFFFFF]',
          },
        },
        SingleValue: {
          props: {
            className: 'w-full',
          },
        },
        IconsContainer: {
          props: {
            className: 'text-[#FFFFFF]',
          },
        },
        ClearIcon: {
          component: () => null, // Hide the clear icon
        },
        Popover: {
          props: {
            className: 'Popover !bg-transparent',
            overrides: {
              Body: {
                props: {
                  className: 'Body !bg-transparent',
                },
              },
              Inner: {
                props: {
                  className: 'Inner !bg-transparent',
                },
              },
            },
          },
        },
        DropdownContainer: {
          props: {
            className: 'DropdownContainer !bg-none mt-1 mb-1',
          },
        },
        Dropdown: {
          props: {
            className: 'Dropdown !border !border-[#B5B5B5] !rounded-main',
          },
          style: () => ({
            maxHeight: '300px',
            backgroundColor: '#111111',
          }),
        },
      }}
      onChange={(params) => {
        const option = params.value[0] as DynaVault;
        props.onChange(option.address);
      }}
    />
  );
};

// eslint-disable-next-line import/no-default-export
export default InputVaultSelect;

const VaultApy = (props: { vault: DynaVault; className?: string }) => {
  const { vault, className } = props;

  const apy = getVaultAPY(vault);
  const apyPrefix = getVaultAPYPrefix(vault, apy);

  return (
    <Body2
      className={cx(
        twMerge('font-bold', apy > 0 ? 'text-green' : 'text-white', className)
      )}
    >
      {apyPrefix}
      {formatPercent(apy.toString())}%
    </Body2>
  );
};
